import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  isGoogleChrome,
  shouldPromptUseChromeFromUserAgentOnly
} from '../utils/googleChromeDetection';

/** Bump when prompt logic changes so QA/dev isn’t stuck with an old “dismissed” flag. */
const SESSION_KEY = 'closer_chrome_toast_dismissed_v2';

function readDismissedFromSession(): boolean {
  try {
    return sessionStorage.getItem(SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * When the browser is not Google Chrome, shows a dismissible toast until the user closes it
 * (remembered for the rest of the tab session).
 *
 * Show rule: user dismissed → hide. Else show if **UA says “not Chrome”** OR **async probe says not Chrome**.
 * If UA already says Safari/Firefox/etc., we never hide the toast just because async wrongly returned “Chrome”
 * (Safari can report misleading Client Hints).
 */
export function useChromeRequiredGate(): {
  showChromeToast: boolean;
  dismissChromeToast: () => void;
} {
  const [dismissed, setDismissed] = useState(readDismissedFromSession);

  const syncShouldPrompt = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    return shouldPromptUseChromeFromUserAgentOnly(navigator.userAgent, navigator.vendor);
  }, []);

  const [asyncIsChrome, setAsyncIsChrome] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    void isGoogleChrome()
      .then((ok) => {
        if (cancelled) return;
        setAsyncIsChrome(ok);
      })
      .catch(() => {
        if (cancelled) return;
        setAsyncIsChrome(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const showChromeToast =
    !dismissed &&
    (syncShouldPrompt || (asyncIsChrome === false));

  const dismissChromeToast = useCallback(() => {
    try {
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch {
      /* ignore private mode / quota */
    }
    setDismissed(true);
  }, []);

  return {
    showChromeToast,
    dismissChromeToast
  };
}
